import { Link } from '@mui/material';
import { dateFormatter, getFormattedRuPhone } from 'core/utils/commonUtils';
import { isEmpty, isNil } from 'lodash';
import { ClientTypeEnum } from 'models/enums/clientTypeEnum';
import { useMemo } from 'react';
import ResponsiveKeyValueList from './ResponsiveKeyValueList';
import type { Row } from './ResponsiveKeyValueList';
import type { Client } from 'models/client/client';
import type { FC } from 'react';

interface Field {
  label: string;
  value: string;
  types: ClientTypeEnum[];
}

const filterFields = (fields: Field[], client: Client): Field[] =>
  fields.filter((f) => !isEmpty(f.value) && f.types.includes(client.type));

const getRequisiteFields = (client: Client): Field[] => [
  {
    label: 'ИНН',
    value: client.inn,
    types: [ClientTypeEnum.Entity, ClientTypeEnum.IndividualEntrepreneur],
  },
  { label: 'КПП', value: client.kpp, types: [ClientTypeEnum.Entity] },
  { label: 'ОГРН', value: client.ogrn, types: [ClientTypeEnum.Entity] },
  {
    label: 'Наименование',
    value: client.fullName,
    types: [ClientTypeEnum.Entity],
  },
  {
    label: 'Руководитель',
    value: client.managementFullName,
    types: [ClientTypeEnum.Entity],
  },
  {
    label: 'Юридический адрес',
    value: client.entityAddress,
    types: [ClientTypeEnum.Entity],
  },
  {
    label: 'Расчетный счет',
    value: client.paymentAccount,
    types: [ClientTypeEnum.Entity, ClientTypeEnum.IndividualEntrepreneur],
  },
];

const getBankFields = (client: Client): Field[] => [
  {
    label: 'БИК банка',
    value: client.bankBIC,
    types: [ClientTypeEnum.Entity, ClientTypeEnum.IndividualEntrepreneur],
  },
  {
    label: 'Название банка',
    value: client.bankName,
    types: [ClientTypeEnum.Entity, ClientTypeEnum.IndividualEntrepreneur],
  },
  {
    label: 'ИНН банка',
    value: client.bankINN,
    types: [ClientTypeEnum.Entity, ClientTypeEnum.IndividualEntrepreneur],
  },
  {
    label: 'КПП банка',
    value: client.bankKPP,
    types: [ClientTypeEnum.Entity, ClientTypeEnum.IndividualEntrepreneur],
  },
  {
    label: 'Корреспондентский счет банка',
    value: client.bankCorrespondentAccount,
    types: [ClientTypeEnum.Entity, ClientTypeEnum.IndividualEntrepreneur],
  },
];

const getDocFields = (client: Client): Field[] => [
  {
    label: 'Серия/номер',
    value: `${client.docSeries ?? ''} ${client.docNumber ?? ''}`,
    types: [ClientTypeEnum.Individual, ClientTypeEnum.IndividualEntrepreneur],
  },
  {
    label: 'Выдан',
    value: client.docIssuedBy ?? '',
    types: [ClientTypeEnum.Individual, ClientTypeEnum.IndividualEntrepreneur],
  },
  {
    label: 'Дата выдачи',
    value: isNil(client.docDateOfIssue)
      ? ''
      : dateFormatter.format(client.docDateOfIssue),
    types: [ClientTypeEnum.Individual, ClientTypeEnum.IndividualEntrepreneur],
  },
  {
    label: 'Адрес регистрации',
    value: client.registrationAddress,
    types: [ClientTypeEnum.Individual, ClientTypeEnum.IndividualEntrepreneur],
  },
];

const ClientView: FC<ClientViewProps> = ({ client }) => {
  const isEntity = client?.type === ClientTypeEnum.Entity;
  const isIndividualEntrepreneur = client?.type === ClientTypeEnum.IndividualEntrepreneur;

  const requisites = useMemo(() => {
    if (isNil(client)) return [];
    const fields = getRequisiteFields(client);

    return filterFields(fields, client);
  }, [client]);

  const bankData = useMemo(() => {
    if (isNil(client)) return [];
    const fields = getBankFields(client);

    return filterFields(fields, client);
  }, [client]);

  const docData = useMemo(() => {
    if (isNil(client)) return [];
    const fields = getDocFields(client);

    return filterFields(fields, client);
  }, [client]);

  const contactsData: Row[] = useMemo(() => {
    if (isNil(client)) return [];
    return [
      !isEmpty(client.phone) && {
        label: 'Телефон',
        value: (
          <Link href={`tel:+${client.phone}`}>{getFormattedRuPhone(client.phone)}</Link>
        ),
      },
      !isEmpty(client.email) && {
        label: 'Email',
        value: <Link href={`mailto:${client.email}`}>{client.email}</Link>,
      },
    ].filter((v) => v) as Row[];
  }, [client]);

  if (isNil(client)) return <></>;

  return (
    <>
      {contactsData.length > 0 && (
        <ResponsiveKeyValueList title='Контакты' rows={contactsData} />
      )}

      {!isEntity && docData.length > 0 && (
        <ResponsiveKeyValueList title='Паспорт РФ' rows={docData} />
      )}
      {(isEntity || isIndividualEntrepreneur) && (
        <>
          {requisites.length > 0 && (
            <ResponsiveKeyValueList title='Реквизиты' rows={requisites} />
          )}
          {bankData.length > 0 && (
            <ResponsiveKeyValueList title='Реквизиты банка' rows={bankData} />
          )}
        </>
      )}
    </>
  );
};

interface ClientViewProps {
  client?: Client;
}

export default ClientView;
